/**
 * Browser half of staleBundle.ts: reload into the page that was asked for when
 * its lazily loaded code is gone after a deploy. A subpath export, like
 * otel/browser, so the Workers that import this package never see `window`.
 */
import { isStaleChunkError, shouldReloadForStaleBundle } from './staleBundle'

export { isStaleChunkError, shouldReloadForStaleBundle } from './staleBundle'

const LAST_RELOAD_KEY = 'automadoc-stale-bundle-reload'

/** The part of a vue-router Router this needs; the apps pass their router. */
export interface StaleBundleRouter {
  onError(handler: (error: unknown, to?: { fullPath: string }) => void): unknown
  resolve(to: string): { href: string }
}

function readLastReload(): number | null {
  try {
    const raw = sessionStorage.getItem(LAST_RELOAD_KEY)
    return raw === null ? null : Number(raw)
  } catch {
    return null
  }
}

function writeLastReload(now: number): void {
  try {
    sessionStorage.setItem(LAST_RELOAD_KEY, String(now))
  } catch {
    // Storage blocked: the reload still happens, only the loop guard is lost
    // for this tab — the same as before this existed, plus one reload.
  }
}

/**
 * Reloads the page when a navigation fails because the page's code could not be
 * loaded, landing on the route that was asked for. Returns whether it reloaded.
 */
export function reloadForStaleBundle(targetHref?: string): boolean {
  const now = Date.now()
  if (!shouldReloadForStaleBundle(readLastReload(), now)) return false
  writeLastReload(now)
  if (targetHref) {
    const url = new URL(window.location.href)
    // Hash history gives "#/signatures"; history mode a path.
    if (targetHref.startsWith('#')) url.hash = targetHref
    else url.href = new URL(targetHref, url).href
    // Changing only the hash does not reload, so set the address, then reload.
    window.history.replaceState(window.history.state, '', url.href)
  }
  window.location.reload()
  return true
}

export function installStaleBundleReload(router: StaleBundleRouter): void {
  router.onError((error, to) => {
    if (!isStaleChunkError(error)) return
    reloadForStaleBundle(to ? router.resolve(to.fullPath).href : undefined)
  })
}
